import { useMemo } from "react"
import { flatJoin, flatten } from "shared/util"
import { ArticleResult } from "interface/article"
import { Typography } from "@mui/material"
import { GridRowId } from "@mui/x-data-grid"
import { GridColumnDefMap } from "components/grid/types"
import { columnBase } from "components/grid/util"
import { renderer } from "components/grid/renderer"
import { formatJournalDate, getJournalDate } from "features/article/util"

type useArticleGridColumnsProps = {
	onClickLink: (id: GridRowId) => void
}
export const useArticleGridColumns = ({
	onClickLink
}: useArticleGridColumnsProps) => {
	const columnsDef = useMemo<GridColumnDefMap<ArticleResult>>(
		() => {
			const columnsDef: GridColumnDefMap<ArticleResult> = {
				'link': {
					headerName: '詳細',
					hideable: false,
					sticky: true,
					value: (r) => r.articleId,
					...columnBase.action.openById(onClickLink),
				},
				'source': {
					headerName: '掲載元',
					width: 90,
					value: (r) => (
						r.source === 'pubmed' ? 'PubMed' :
						r.source === 'ichusi' ? '医中誌' : r.source
					),
					filterType: 'candidates'
				},
				'linkId': {
					headerName: '論文ID',
					width: 90,
					value: (r) => r.linkId,
					filterType: 'text'
				},
				'title': {
					headerName: 'タイトル',
					width: 500,
					sticky: true,
					value: (r) => flatten([r.titleJpn, r.title]),
					renderCell: (params) => {
						const value = params.value
						if (!value || typeof value !== 'string' || value === '') return <></>
						const at = value.indexOf('\n')
						if (at < 0) return <div translate="yes">{value}</div>
						const jpn = value.substring(0, at)
						const eng = value.substring(at + 1)
						return <div>
							<Typography component="div" variant="body2">{jpn}</Typography>
							<Typography component="div" variant="caption" color="text.secondary">{eng}</Typography>
						</div>
					},
					filterType: 'sentense'
				},
				'publicationType': {
					headerName: 'Type',
					width: 100,
					value: (r) => r.publicationType?.map(type => type.name),
					renderCell: (params) => renderer.multiRow(params.value),
					filterType: 'candidates'
				},
				'journal': {
					headerName: 'Journal',
					width: 300,
					value: (r) => flatJoin(' ', r.journal.title, r.journal.issue, r.pagination),
					filterField: 'journalTitle'
				},
				'journalTitle': {
					headerName: 'Journal title',
					width: 200,
					defaultShow: false,
					value: (r) => r.journal.title,
					filterType: 'candidates'
				},
				'issue': {
					headerName: 'Issue',
					width: 100,
					defaultShow: false,
					value: (r) => r.journal.issue,
					filterType: 'sentense'
				},
				'page': {
					headerName: 'Page',
					width: 200,
					defaultShow: false,
					value: (r) => r.pagination,
					filterType: 'sentense'
				},
				'date': {
					headerName: 'Date',
					width: 130,
					value: (r) => getJournalDate(r),
					valueFormatter: (_, row: { dateText?: string }) => row.dateText ?? '',
					filterType: 'date'
				},
				'dateText': {
					headerName: 'DateText',
					hidden: true,
					value: (r) => formatJournalDate(r)
				},
				'abstract': {
					headerName: 'Abstract',
					width: 500,
					defaultShow: false,
					value: (r) => r.abstractJpn ?? r.abstract,
					renderCell: (params) => renderer.clampText(params.value, 5),
					filterType: 'sentense'
				},
				'meshs': {
					headerName: 'Mesh',
					width: 200,
					defaultShow: false,
					value: (r) => r.meshs?.flatMap(mesh => mesh.isMajor ? (mesh.descriptionJpn ?? mesh.description) : []),
					renderCell: (params) => renderer.multiRow(params.value, 'vertical', 5),
					filterType: 'candidates'
				},
				'authors': {
					headerName: '著者',
					width: 500,
					defaultShow: false,
					value: (r) => r.authors?.map(author => author.authorName),
					renderCell: (params) => renderer.multiRow(params.value, 'horizon'),
					filterType: 'text'
				}
			}
			return columnsDef
		},
		[onClickLink]
	)
	return { columnsDef }
}